import React, { useRef } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import ScienceIcon from '@mui/icons-material/Science';
import { Box, Chip, Typography } from '@mui/material';
import { useSessionReplayCapture } from '@services/statsig';
import { useModal } from '@store/hooks';

import {
  type ActionCommand,
  ActionCommandType,
  type BaseAction,
  type CardContentProps,
} from '../../types';

import CardContent from './base/CardContent';

interface LabOrderSuggestionCardContentProps extends CardContentProps {
  action: BaseAction<'lab_order_suggestion'>;
}

const LabOrderSuggestionCardContent: React.FC<LabOrderSuggestionCardContentProps> = ({
  action,
  onCommand,
}) => {
  const { startCapture } = useSessionReplayCapture();
  const { open: openModal } = useModal();
  const hasHoveredRef = useRef(false);

  const labOrderAction = action;
  const payload = labOrderAction.action_payload;
  const labOrder = payload.lab_order;

  // Helper to convert lab order data to CreateLabOrderRequest format
  const getLabOrderFormData = () => ({
    patient_id: labOrder.patient_id,
    lab_tests: labOrder.lab_tests,
    icd_10_codes: labOrder.icd_10_codes,
    special_instructions: labOrder.special_instructions,
    sign_with_name: labOrder.sign_with_name,
  });

  const content = (
    <>
      {labOrderAction.patient?.name && (
        <Typography variant="body2" color="text.secondary">
          {labOrderAction.patient.name}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          py: 1,
          alignItems: 'flex-start',
        }}
      >
        {labOrder.bundle_names && labOrder.bundle_names.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {labOrder.bundle_names.map((bundle, index) => (
              <Chip key={index} label={bundle} size="small" variant="outlined" color="primary" />
            ))}
          </Box>
        )}
        {labOrder.lab_tests && labOrder.lab_tests.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {labOrder.lab_tests.length} test{labOrder.lab_tests.length > 1 ? 's' : ''} suggested
          </Typography>
        )}
      </Box>
    </>
  );

  const handleCardClick = () => {
    // Open lab order modal for editing without auto-generating PDF
    openModal(MODAL_TYPES.LAB_ORDER, {
      patientId: labOrder.patient_id,
      initialFormData: getLabOrderFormData(),
      shouldAutoGeneratePDF: false,
      maxWidth: 'lg',
      // Add continuation action to accept the action after PDF generation
      continuationAction: {
        type: 'actions/ui/setPendingCommand',
        payload: {
          actionId: action.action_id,
          command: {
            type: ActionCommandType.ACCEPT,
          },
        },
      },
    });
  };

  const handleCommand = (command: ActionCommand) => {
    if (command.type === ActionCommandType.ACCEPT) {
      // Open lab order modal with auto-generate PDF flag
      openModal(MODAL_TYPES.LAB_ORDER, {
        patientId: labOrder.patient_id,
        initialFormData: getLabOrderFormData(),
        shouldAutoGeneratePDF: true,
        maxWidth: 'lg',
        // Add continuation action to accept after successful PDF generation
        continuationAction: {
          type: 'actions/ui/setPendingCommand',
          payload: {
            actionId: action.action_id,
            command: {
              type: ActionCommandType.ACCEPT,
            },
          },
        },
      });
    } else if (
      command.type === ActionCommandType.EDIT ||
      command.type === ActionCommandType.EDIT_IN_MODAL
    ) {
      handleCardClick();
    } else {
      // Pass through other commands (reject, etc.)
      onCommand(command);
    }
  };

  return (
    <CardContent
      id={action.action_id}
      title="Create Lab Order?"
      content={content}
      avatarIcon={<ScienceIcon />}
      onMouseOver={() => {
        if (!hasHoveredRef.current) {
          hasHoveredRef.current = true;
          startCapture('lab_order_suggestion_action_card_hover');
        }
      }}
      onCommand={handleCommand}
      expandable={false}
      showExpandIcon={true}
      isExpandableContentEditable={true}
      onCardClick={handleCardClick}
    />
  );
};

export default LabOrderSuggestionCardContent;
